import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserContext } from "../../utils/contexts/UserContext";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import {
  DASHBOARD_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";
import { getAvailableMaterials } from "../../services/materialsService";
import MaterialTeacherCard from "./MaterialTeacherCard";
import RecommendedMaterials from "./RecommendedMaterials";
import { MaterialCardSkeleton } from "./MaterialPreview";

const PAGE_SIZE = 20;

const TABS = ["recommended", "all", "saved"];

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <MaterialCardSkeleton key={i} />
      ))}
    </div>
  );
}

function EmptyState({ message }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50">
      <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </span>
      <p className="text-sm font-bold text-slate-700">{t("materials.emptyTitle")}</p>
      <p className="mt-1 text-xs text-slate-400 max-w-xs">{message}</p>
    </div>
  );
}

function CardGrid({ entries }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {entries.map((entry) => (
        <MaterialTeacherCard
          key={entry.material.uuid}
          material={entry.material}
          matchedQuestions={entry.matched_questions || []}
        />
      ))}
    </div>
  );
}

// Shared prev/next pager for the All and Saved tabs (both page server-side).
function Pager({ page, totalPages, loading, onPrev, onNext, t }) {
  if (totalPages <= 1) return null;
  const btn =
    "px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="mt-6 flex items-center justify-between">
      <span className="text-xs text-slate-500">
        {t("materials.pageOf", { page, total: totalPages })}
      </span>
      <div className="flex items-center gap-2">
        <button type="button" disabled={page <= 1 || loading} onClick={onPrev} className={btn}>
          {t("materials.prev")}
        </button>
        <button
          type="button"
          disabled={page >= totalPages || loading}
          onClick={onNext}
          className={btn}
        >
          {t("materials.next")}
        </button>
      </div>
    </div>
  );
}

export default function MaterialsLibrary() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  const canView = hasPermission(user, DASHBOARD_PERMISSION_CODES.TEACHER_VIEW);

  const [tab, setTab] = useState("recommended");

  // Available (paginated list of MaterialRead). Powers both the "All" and
  // "Saved" tabs — Saved is a client-side filter of the loaded page.
  const [available, setAvailable] = useState([]);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  // Reset to the first page whenever the search term OR the tab changes (the
  // Saved tab is its own server-side query, so a stale page would 404).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tab]);

  const loadAvailable = useCallback(
    (signal) => {
      setAvailableLoading(true);
      getAvailableMaterials({
        // On the Saved tab, page over the caller's saved materials server-side
        // instead of client-filtering one page (which hid saved items past p1).
        params: {
          search: debouncedSearch || undefined,
          page,
          saved: tab === "saved" ? "true" : undefined,
        },
        signal,
      })
        .then((data) => {
          setAvailable(data?.results || []);
          setAvailableTotal(data?.count ?? 0);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
          toast.error(t("materials.loadFailed"));
        })
        .finally(() => setAvailableLoading(false));
    },
    [debouncedSearch, page, tab, t],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadAvailable(controller.signal);
    return () => controller.abort();
  }, [loadAvailable]);

  // Wrap available MaterialRead rows so they share the card-entry shape.
  const availableEntries = useMemo(
    () => available.map((m) => ({ material: m })),
    [available],
  );
  const totalPages = Math.max(1, Math.ceil(availableTotal / PAGE_SIZE));
  const showSearch = tab === "all" || tab === "saved";

  // Deep-link guard for users who reach /my-materials without the permission.
  if (!canView) return <Navigate to="/" replace />;

  const tabLabel = (key) =>
    ({
      recommended: t("materials.tabRecommended"),
      all: t("materials.tabAll"),
      saved: t("materials.tabSaved"),
    })[key];

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl max-w-full overflow-x-auto">
            {TABS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition ${
                  tab === key
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tabLabel(key)}
              </button>
            ))}
          </div>

          {showSearch && (
            <div className="relative">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.search")}
                className="pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none w-44 sm:w-56 transition"
              />
            </div>
          )}
        </div>

        {tab === "recommended" && (
          <RecommendedMaterials
            user={user}
            gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            loadingFallback={<SkeletonGrid />}
            emptyFallback={
              <EmptyState message={t("materials.recommendedEmpty")} />
            }
            onError={() => toast.error(t("materials.loadFailed"))}
          />
        )}

        {tab === "all" &&
          (availableLoading && available.length === 0 ? (
            <SkeletonGrid />
          ) : availableEntries.length === 0 ? (
            <EmptyState message={t("materials.allEmpty")} />
          ) : (
            <>
              <CardGrid entries={availableEntries} />
              <Pager
                page={page}
                totalPages={totalPages}
                loading={availableLoading}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                t={t}
              />
            </>
          ))}

        {tab === "saved" &&
          (availableLoading && available.length === 0 ? (
            <SkeletonGrid />
          ) : availableEntries.length === 0 ? (
            <EmptyState message={t("materials.savedEmpty")} />
          ) : (
            <>
              <CardGrid entries={availableEntries} />
              <Pager
                page={page}
                totalPages={totalPages}
                loading={availableLoading}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                t={t}
              />
            </>
          ))}
      </div>
    </div>
  );
}
